import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import loiPrimaryEditIconUrl from '@/assets/information-2-line 2.svg'
import loiUpcomingEditIconUrl from '@/assets/pencil-line 11.svg'
import type { LineOfInquiry } from '../types'
import {
  loiBodyColor,
  loiCardBorderColor,
  loiCardInactiveSurface,
  loiCardPrimaryInnerBg,
  loiCardPrimaryOuterBg,
  loiInstrumentTagChipRowSx,
  loiRankBadgeChipSx,
  loiTitleColor,
} from './loiFigmaLoiCardStyles'
import { libraryCardHoverSx } from '@/shared/lib/libraryCardHover'

export type LoiCardProps = {
  readonly loi: LineOfInquiry
  readonly rankIndex: number
  readonly onEdit: () => void
  readonly editDisabled: boolean
  readonly dragDisabled: boolean
  readonly dragHandleProps: Record<string, unknown>
  readonly isDragging: boolean
}

export function LoiCard({
  loi,
  rankIndex,
  onEdit,
  editDisabled,
  dragDisabled,
  dragHandleProps,
  isDragging,
}: LoiCardProps) {
  const isPrimary = rankIndex === 0
  const editIconSrc = isPrimary
    ? loiPrimaryEditIconUrl
    : loiUpcomingEditIconUrl

  return (
    <Card
      variant="outlined"
      data-testid={`loi-card-${loi.id}`}
      sx={{
        borderRadius: '10px',
        borderColor: loiCardBorderColor,
        bgcolor: isPrimary ? loiCardPrimaryOuterBg : loiCardInactiveSurface,
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? 3 : '0px 1px 1px rgba(0,0,0,0.05)',
        ...libraryCardHoverSx,
        '&:hover': {
          borderWidth: '1.8px',
          borderStyle: 'solid',
          borderColor: loiCardBorderColor,
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          bgcolor: isPrimary ? loiCardPrimaryInnerBg : 'transparent',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <IconButton
            size="small"
            aria-label="Reorder line of inquiry"
            disabled={dragDisabled}
            data-testid={`loi-drag-${loi.id}`}
            sx={{
              mt: 0.25,
              color: dragDisabled ? 'action.disabled' : 'text.secondary',
              cursor: dragDisabled ? 'default' : 'grab',
            }}
            {...dragHandleProps}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography
                component="h3"
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: loiTitleColor,
                }}
              >
                {loi.title}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  size="small"
                  label={isPrimary ? 'Primary' : 'Up Next'}
                  sx={loiRankBadgeChipSx(isPrimary)}
                />
                <IconButton
                  size="small"
                  aria-label="Edit line of inquiry"
                  onClick={onEdit}
                  disabled={editDisabled}
                  data-testid={`loi-edit-${loi.id}`}
                >
                  <Box
                    component="img"
                    src={editIconSrc}
                    alt=""
                    sx={{
                      width: 20,
                      height: 20,
                      display: 'block',
                      objectFit: 'contain',
                      opacity: editDisabled ? 0.38 : 1,
                    }}
                  />
                </IconButton>
              </Stack>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                lineHeight: '20px',
                color: loiBodyColor,
                mb: 1.5,
              }}
            >
              {loi.description}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
              {loi.instrumentTags.map((tag) => (
                <Chip
                  key={`${loi.id}-${tag}`}
                  label={tag}
                  size="small"
                  sx={loiInstrumentTagChipRowSx(isPrimary)}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
